import { formatMoney } from '@theme/money-formatting';

/**
 * <cij-sale> — the Christmas in July sale layer.
 *
 * A deliberately small vanilla element (not a theme Component: it renders no
 * refs and must never intercept the PDP's declarative events) used by both
 * sections/christmas-july-pdp.liquid and sections/christmas-july-hero.liquid.
 * It owns three sale-only behaviors, each engaging only when its markup hooks
 * exist inside the surrounding .shopify-section:
 *
 * 1. Click-to-copy promo code — any [data-cij-copy] button in the section.
 * 2. With-code price sync — recomputes the "$46.74 with code" figures when
 *    the flavor selection changes. <everyday-pdp> updates [data-ref="mainPrice"]
 *    and the form's hidden variant input synchronously on every selection
 *    (see assets/everyday-pdp.js #commit), so a MutationObserver on the price
 *    node fires after both are already correct — no coupling to the PDP's
 *    event timing, no money-string parsing (prices come from the same
 *    [data-pdp-data] payload the PDP element reads).
 * 3. Countdown — fills [data-cij-countdown] from data-sale-end (YYYY-MM-DD,
 *    ends end-of-day in the shopper's timezone); stays hidden when the date
 *    is absent, unparsable, or past.
 *
 * @typedef {{ id: number, price: number }} CijVariant
 */
class CijSale extends HTMLElement {
  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {MutationObserver | undefined} */
  #priceObserver;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #countdownTimer;

  connectedCallback() {
    this.#listeners = new AbortController();
    const section = this.closest('.shopify-section') || this;

    // ---- 1. click-to-copy (delegated so the deal panel's ticket works too) ----
    section.addEventListener('click', this.#onSectionClick, { signal: this.#listeners.signal });

    // ---- 2. with-code price sync ----
    const priceEl = section.querySelector('[data-ref="mainPrice"]');
    if (priceEl) {
      this.#syncPrices(section);
      this.#priceObserver = new MutationObserver(() => this.#syncPrices(section));
      this.#priceObserver.observe(priceEl, { childList: true, characterData: true, subtree: true });
    }

    // ---- 3. countdown ----
    const countdown = section.querySelector('[data-cij-countdown]');
    if (countdown instanceof HTMLElement && this.dataset.saleEnd) {
      const tick = () => this.#renderCountdown(countdown);
      tick();
      this.#countdownTimer = setInterval(tick, 30000);
    }
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#priceObserver?.disconnect();
    if (this.#countdownTimer) clearInterval(this.#countdownTimer);
  }

  /** @param {Event} event */
  #onSectionClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-cij-copy]') : null;
    if (!(target instanceof HTMLElement)) return;
    const code = target.dataset.code || this.dataset.code || '';
    if (!code) return;
    this.#copy(code, target);
  };

  /**
   * @param {string} code
   * @param {HTMLElement} button
   */
  async #copy(code, button) {
    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      // clipboard API can be unavailable (permissions, non-secure preview);
      // fall back to a transient selected textarea
      const scratch = document.createElement('textarea');
      scratch.value = code;
      scratch.setAttribute('readonly', '');
      scratch.style.position = 'fixed';
      scratch.style.opacity = '0';
      document.body.appendChild(scratch);
      scratch.select();
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }
      scratch.remove();
    }
    if (!copied) return;

    button.dataset.copied = 'true';
    setTimeout(() => button.removeAttribute('data-copied'), 2000);

    const detail = { code, source: this.dataset.source || 'cij-sale' };
    document.dispatchEvent(new CustomEvent('cij:copy_code', { detail, bubbles: true }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'cij:copy_code', ...detail });
  }

  /**
   * Recompute every with-code figure in the section from the selected
   * variant's real price. Reads the PDP payload lazily each time — it is
   * static JSON rendered by Liquid, so a single parse is cached.
   * @param {Element} section
   */
  #syncPrices(section) {
    const percent = Number(this.dataset.percent) || 0;
    if (percent <= 0) return;

    const idInput = section.querySelector('product-form-component input[name="id"]');
    if (!(idInput instanceof HTMLInputElement)) return;

    const variant = this.#variants(section).find((v) => v.id === Number(idInput.value));
    if (!variant) return;

    const codeCents = Math.round((variant.price * (100 - percent)) / 100);
    const savedCents = variant.price - codeCents;

    for (const el of section.querySelectorAll('[data-cij-code-price]')) {
      el.textContent = this.#money(codeCents);
    }
    for (const el of section.querySelectorAll('[data-cij-savings]')) {
      el.textContent = this.#money(savedCents);
    }
  }

  /** @param {Element} section @returns {CijVariant[]} */
  #variants(section) {
    if (this.#variantCache) return this.#variantCache;
    const payload = section.querySelector('script[data-pdp-data]');
    if (!payload?.textContent) return [];
    try {
      const data = JSON.parse(payload.textContent);
      this.#variantCache = Array.isArray(data.variants) ? data.variants : [];
      this.#moneyFormat = typeof data.moneyFormat === 'string' ? data.moneyFormat : '';
    } catch {
      this.#variantCache = [];
    }
    return this.#variantCache;
  }

  /** @type {CijVariant[] | undefined} */
  #variantCache;

  /** @type {string} */
  #moneyFormat = '';

  /** @param {number} cents */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    if (this.#moneyFormat) return formatMoney(Math.round(cents), this.#moneyFormat, currency);
    return `$${(cents / 100).toFixed(2)}`;
  }

  /** @param {HTMLElement} countdown */
  #renderCountdown(countdown) {
    const raw = this.dataset.saleEnd || '';
    const match = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      countdown.hidden = true;
      return;
    }
    // sale runs through the end of that day, shopper-local
    const end = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59);
    const remaining = end.getTime() - Date.now();
    if (Number.isNaN(remaining) || remaining <= 0) {
      countdown.hidden = true;
      return;
    }

    const minutes = Math.floor(remaining / 60000);
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    const label =
      days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${mins}m` : `${Math.max(mins, 1)}m`;

    const prefix = this.dataset.countdownPrefix || 'Sale ends in';
    countdown.textContent = `${prefix} ${label}`;
    countdown.hidden = false;
  }
}

if (!customElements.get('cij-sale')) {
  customElements.define('cij-sale', CijSale);
}
